/**
 * The free-text classes a user named (LBL-08), by shape type: offered in their tools, after the
 * catalog's labels, until they log out. Kept in this browser per user, so every tab offers the same
 * ones; logging out clears them, and a sign-in keeps only the user's own, so a shared computer never
 * offers one user's names to the next.
 */
import { useSyncExternalStore } from 'react'

import { forgetStored, readStored } from '../lib/stored'
import type { LabelType } from '../lib/types'

export type NamedClasses = Readonly<Partial<Record<LabelType, readonly string[]>>>

const PREFIX = 'pedfx.freeTextClasses:'
const NONE: NamedClasses = {}
const listeners = new Set<() => void>()
let cached: { key: string; raw: string | null; named: NamedClasses } | undefined

function parsed(raw: string | null): NamedClasses {
  try {
    const found: unknown = JSON.parse(raw ?? '{}')
    if (typeof found !== 'object' || found === null) return NONE
    return Object.fromEntries(
      Object.entries(found).filter((entry): entry is [LabelType, string[]] => Array.isArray(entry[1]) && entry[1].every((name) => typeof name === 'string')),
    )
  } catch {
    return NONE
  }
}

/** The classes the user named, by shape type. */
export function namedClasses(userId: string): NamedClasses {
  const key = `${PREFIX}${userId}`
  const raw = readStored(key)
  if (cached?.key !== key || cached.raw !== raw) cached = { key, raw, named: parsed(raw) }
  return cached.named
}

function subscribe(listener: () => void): () => void {
  // Another tab names a class or logs out.
  const stored = (event: StorageEvent) => {
    if (event.key === null || event.key.startsWith(PREFIX)) listener()
  }
  listeners.add(listener)
  window.addEventListener('storage', stored)
  return () => {
    listeners.delete(listener)
    window.removeEventListener('storage', stored)
  }
}

export function useNamedClasses(userId: string): NamedClasses {
  return useSyncExternalStore(subscribe, () => namedClasses(userId))
}

/** Offers the class in the user's tools of its type from now on, after those named before. */
export function nameClass(userId: string, type: LabelType, name: string): void {
  const named = namedClasses(userId)
  if (named[type]?.includes(name)) return
  try {
    localStorage.setItem(`${PREFIX}${userId}`, JSON.stringify({ ...named, [type]: [...(named[type] ?? []), name] }))
  } catch {
    return // storage blocked: the class is drawn, but not offered again
  }
  for (const listener of listeners) listener()
}

/** Forgets the names of every user but `keep`, of all of them without one (at logout). */
export function forgetNamedClasses(keep?: string): void {
  forgetStored((key) => key.startsWith(PREFIX) && key !== `${PREFIX}${keep}`)
  for (const listener of listeners) listener()
}
